/**
 * [INPUT]: 依赖浏览器 createImageBitmap / Canvas / ImageData
 * [OUTPUT]: 对外提供 loadAndPreprocess(file) → { canvas, width, height, quality } 与 ImageQuality
 * [POS]: apps/web/pipeline 的预处理层，解码+EXIF 纠正+缩放+光照/模糊估计；对齐 docs §2.3 step1
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../../CLAUDE.md
 */
import { PIPELINE_CONFIG } from './config'

export interface ImageQuality {
  brightness: number // 0..255 均值
  brightnessVar: number // 0..255 方差
  blurLaplacianVar: number // Laplacian 方差（越高越清晰）
  score: number // 0..1 综合照片质量
}

export interface PreprocessedImage {
  canvas: HTMLCanvasElement
  width: number
  height: number
  quality: ImageQuality
}

/** 校验文件类型与大小，返回 RejectCode-like 或 null。 */
export function validateFile(file: File): 'INVALID_FILE' | null {
  const accepted = PIPELINE_CONFIG.image.acceptedMime
  if (!(accepted as readonly string[]).includes(file.type)) return 'INVALID_FILE'
  if (file.size > PIPELINE_CONFIG.image.maxBytes) return 'INVALID_FILE'
  return null
}

/** 解码 + EXIF 方向纠正 + 最长边缩放到 1024，返回 canvas 与质量估计。 */
export async function loadAndPreprocess(file: File): Promise<PreprocessedImage> {
  // imageOrientation: 'from-image' 自动应用 EXIF 方向
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })

  const maxEdge = PIPELINE_CONFIG.image.longestEdge
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height))
  const width = Math.max(1, Math.round(bitmap.width * scale))
  const height = Math.max(1, Math.round(bitmap.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!
  ctx.drawImage(bitmap, 0, 0, width, height)
  bitmap.close?.()

  const imageData = ctx.getImageData(0, 0, width, height)
  const quality = estimateQuality(imageData)
  return { canvas, width, height, quality }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v))
}

/** 光照（亮度均值/方差）+ 模糊（Laplacian 方差）估计，合成 0..1 照片质量。 */
export function estimateQuality(imageData: ImageData): ImageQuality {
  const { data, width, height } = imageData
  // 降采样到约 256 长边的灰度，加速 Laplacian
  const maxE = 256
  const s = Math.min(1, maxE / Math.max(width, height))
  const w = Math.max(1, Math.round(width * s))
  const h = Math.max(1, Math.round(height * s))
  const gray = new Float32Array(w * h)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const sx = Math.min(width - 1, Math.floor(x / s))
      const sy = Math.min(height - 1, Math.floor(y / s))
      const i = (sy * width + sx) * 4
      gray[y * w + x] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
    }
  }

  // 亮度均值/方差
  let sum = 0
  let sumSq = 0
  for (let i = 0; i < gray.length; i++) {
    sum += gray[i]
    sumSq += gray[i] * gray[i]
  }
  const n = gray.length
  const brightness = sum / n
  const brightnessVar = sumSq / n - brightness * brightness

  // Laplacian 方差
  const lap = new Float32Array(w * h)
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x
      lap[i] = gray[i - w] + gray[i + w] + gray[i - 1] + gray[i + 1] - 4 * gray[i]
    }
  }
  let lSum = 0
  let lSumSq = 0
  let lN = 0
  for (let i = 0; i < lap.length; i++) {
    lSum += lap[i]
    lSumSq += lap[i] * lap[i]
    lN++
  }
  const blurLaplacianVar = lN > 0 ? lSumSq / lN - (lSum / lN) ** 2 : 0

  const q = PIPELINE_CONFIG.quality
  const brightnessScore = clamp01(
    1 - Math.min(Math.abs(brightness - q.brightnessMin), Math.abs(brightness - q.brightnessMax)) / 100,
  )
  const varScore = clamp01(brightnessVar >= q.brightnessVarMin ? 1 : brightnessVar / q.brightnessVarMin)
  const blurScore = clamp01(blurLaplacianVar / (q.blurLaplacianVar * 3))

  return {
    brightness,
    brightnessVar,
    blurLaplacianVar,
    score: (brightnessScore + varScore + blurScore) / 3,
  }
}